import { useEffect, useMemo, useRef, useState } from 'react';
import { roleName, winSentence } from '../lib/format';
import { ambienceFor, CROSSFADE_MS, FADE_IN_MS, MEDIA, winImage } from '../lib/media';
import type { Connection } from '../lib/socket';
import type { OverlayState } from '../lib/types';

/**
 * Zeigt einen bereits von MediaMTX bereitgestellten WebRTC-Stream.
 *
 * Der Spieler veröffentlicht seine Kamera per WHIP.
 * Das Overlay öffnet denselben Stream über die MediaMTX-WebRTC-Reader-Seite.
 */
type MediaMTXReader = {
  close: () => void;
};

type MediaMTXReaderConstructor = new (conf: {
  url: string;
  token: string;
  onError?: (error: string) => void;
  onTrack?: (event: RTCTrackEvent) => void;
}) => MediaMTXReader;

declare global {
  interface Window {
    MediaMTXWebRTCReader?: MediaMTXReaderConstructor;
  }
}

let mediaMTXReaderScriptPromise: Promise<void> | null = null;

function loadMediaMTXReader(): Promise<void> {
  if (window.MediaMTXWebRTCReader) {
    return Promise.resolve();
  }

  if (mediaMTXReaderScriptPromise) {
    return mediaMTXReaderScriptPromise;
  }

  mediaMTXReaderScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-mediamtx-reader="true"]',
    );

    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('MediaMTX-Reader konnte nicht geladen werden.')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = '/mediamtx-reader.js';
    script.async = true;
    script.dataset.mediamtxReader = 'true';
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('MediaMTX-Reader konnte nicht geladen werden.'));
    document.head.appendChild(script);
  });

  return mediaMTXReaderScriptPromise;
}

/**
 * Liest einen MediaMTX-WebRTC-Stream authentifiziert über WHEP.
 * Der OBS-Overlay-Stream bleibt absichtlich stumm.
 */
function AdaptiveCamera({
  url,
  title,
  cameraToken,
}: {
  url: string;
  title: string;
  cameraToken: string;
  priority?: 'tile' | 'spotlight' | 'host';
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    let reader: MediaMTXReader | null = null;
    let cancelled = false;

    void loadMediaMTXReader()
      .then(() => {
        if (cancelled) return;

        const Reader = window.MediaMTXWebRTCReader;
        if (!Reader) {
          throw new Error('MediaMTX-Reader ist nicht verfügbar.');
        }

        const streamUrl = new URL(url);
        streamUrl.pathname = `${streamUrl.pathname.replace(/\/+$/, '')}/whep`;
        streamUrl.search = '';
        streamUrl.hash = '';

        reader = new Reader({
          url: streamUrl.toString(),
          token: cameraToken,
          onError: (error) => {
            console.warn(`Kamera "${title}": ${error}`);
          },
          onTrack: (event) => {
            if (cancelled || !videoRef.current) return;

            const stream = event.streams[0];
            if (stream && videoRef.current.srcObject !== stream) {
              videoRef.current.srcObject = stream;
              void videoRef.current.play().catch(() => {});
            }
          },
        });
      })
      .catch((error: unknown) => {
        console.error(`Kamera "${title}" konnte nicht gestartet werden.`, error);
      });

    return () => {
      cancelled = true;
      reader?.close();

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [url, cameraToken, title]);

  return <video ref={videoRef} title={title} autoPlay playsInline muted />;
}

const FRAME_CLASS: Record<string, string> = {
  VILLAGER: 'frame-villager',
  VAMPIRE: 'frame-vampire',
  WITCH: 'frame-witch',
  PRIEST: 'frame-priest',
  SEER: 'frame-seer',
  FOOL: 'frame-fool',
  CURSED: 'frame-cursed',
  VAMPIRE_HUNTRESS: 'frame-vampire-huntress',
  DEMON: 'frame-demon',
};

/**
 * Farben und Hintergrund je Sieger. Liegt unter `/wins/<TYP>.png` ein Bild, wird es genutzt;
 * sonst bleibt der Farbverlauf stehen. So lässt sich später jederzeit ein Bild nachlegen.
 */
const CELEBRATION: Record<string, { colors: string[]; shape: 'diamond' | 'circle' | 'strip' }> = {
  FOOL: { colors: ['#f2c744', '#8e3fb0', '#c2263c', '#f4efe2'], shape: 'diamond' },
  VILLAGE: { colors: ['#8a6034', '#b98d53', '#6f7d3f', '#e3d2b0'], shape: 'strip' },
  VAMPIRE: { colors: ['#b4202f', '#7a0f1c', '#c8a24a', '#f0d79a'], shape: 'strip' },
  LOVERS: { colors: ['#d84f6a', '#f19bb0', '#f7d5dd', '#a81f3c'], shape: 'circle' },
  // Der Dämon: Asche und Schatten, nur hier und da ein Funke Rot.
  DEMON: { colors: ['#0f0f12', '#26262c', '#3c3c44', '#8e1220'], shape: 'strip' },
};

/** Konfetti fällt über den Bildschirm – Farben und Form passen zum Sieger. */
function Confetti({ winner }: { winner: string }) {
  const recipe = CELEBRATION[winner];
  const pieces = useMemo(
    () =>
      recipe
        ? Array.from({ length: 70 }, (_, i) => ({
            left: (i * 37) % 100,
            delay: ((i * 13) % 40) / 10,
            duration: 4 + ((i * 7) % 30) / 10,
            drift: ((i % 5) - 2) * 4,
            color: recipe.colors[i % recipe.colors.length]!,
            size: 8 + (i % 4) * 3,
          }))
        : [],
    [winner, recipe],
  );
  if (!recipe) return null;
  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className={`flake ${recipe.shape}`}
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: recipe.shape === 'strip' ? p.size * 1.8 : p.size,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            ['--drift' as string]: `${p.drift}vw`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Text auf der Banderole. Er bleibt einzeilig und wird bei langen Sätzen kleiner gesetzt, damit
 * immer die ganze Nachricht im blauen Feld steht – abgeschnitten wird nichts.
 */
function BannerText({ text }: { text: string }) {
  // 26 Zeichen passen bei voller Größe ins Feld; darüber hinaus verkleinern wir im selben Verhältnis.
  const size = Math.max(1.9, Math.min(4.6, (26 / Math.max(text.length, 1)) * 4.6));
  return <span style={{ fontSize: `${size.toFixed(2)}cqw` }}>{text}</span>;
}

/** Strichliste wie auf Papier: vier Striche, der fünfte quer darüber. */
function Tally({ count }: { count: number }) {
  if (count <= 0) return null;
  const groups: number[] = [];
  for (let left = count; left > 0; left -= 5) groups.push(Math.min(5, left));
  return (
    <div className="tally" aria-label={`${count} Stimmen`}>
      {groups.map((size, g) => (
        <span className="tally-group" key={g}>
          {Array.from({ length: Math.min(size, 4) }, (_, i) => (
            <span className="tally-mark" key={i} />
          ))}
          {size === 5 && <span className="tally-mark cross" />}
        </span>
      ))}
    </div>
  );
}

/**
 * Feste Browserquelle für OBS. Der Spielleiter sitzt oben in der Mitte, die Runde darunter.
 * Nachts behalten die Spieler ihren Platz am Rand; nur die aufgerufene Rolle fährt in die Mitte
 * und danach an genau dieselbe Stelle zurück.
 */
export function OverlayScreen({
  connection,
  cameraToken,
}: {
  connection: Connection<OverlayState>;
  cameraToken: string;
}) {
  const view = connection.view;
  const [morningRevealed, setMorningRevealed] = useState(false);

  /** Jede Meldung läuft über die Banderole und bleibt dort eine halbe Minute stehen. */
  const [banner, setBanner] = useState<string | null>(null);
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const announce = useRef<(text: string, ms?: number) => void>(() => {});
  announce.current = (text: string, ms = 30000) => {
    if (bannerTimer.current) clearTimeout(bannerTimer.current);
    setBanner(text);
    bannerTimer.current = setTimeout(() => setBanner(null), ms);
  };

  // Die OBS-Browserquelle bleibt absichtlich vollständig stumm.
  // Sounds und Musik werden ausschließlich in den Dashboards abgespielt.

  const spotlightIds = useMemo(
    () => new Set((view?.night?.spotlight ?? []).map((s) => s.playerId)),
    [view?.night?.spotlight],
  );

  // Am Morgen bleiben die nächtlichen Toten zunächst 2 Sekunden verborgen.
  // Danach werden sie sichtbar und die Todesmeldung erscheint für 15 Sekunden.
  const isMorning = view?.phase === 'MORNING_REVEAL';

  useEffect(() => {
    setMorningRevealed(false);

    if (!isMorning) return;

    const reveal = setTimeout(() => {
      setMorningRevealed(true);

      const dead = view?.morning?.deadPlayerIds ?? [];
      const namen = dead.map(
        (id) => view?.players.find((p) => p.id === id)?.name ?? '—',
      );

      if (dead.length > 0) {
        announce.current(
          `Noxbury betrauert: ${namen.join(', ')}`,
          15000,
        );
      }
    }, 2000);

    return () => clearTimeout(reveal);
  }, [isMorning, view?.round]);


  // Urteil und Gleichstand der Tagesabstimmung laufen ebenfalls über die Banderole.
  const verdict = view?.dayVote?.eliminatedId ?? null;
  const tie = view?.dayVote?.revealed && !verdict ? (view?.dayVote?.tieCandidates ?? []).join(',') : '';
  useEffect(() => {
    if (!view) return;
    if (verdict) {
      announce.current(`${view.players.find((p) => p.id === verdict)?.name ?? '—'} wurde verurteilt`);
    } else if (tie) {
      const namen = tie.split(',').map((id) => view.players.find((p) => p.id === id)?.name ?? '—');
      announce.current(`Gleichstand: ${namen.join(', ')}`);
    }
  }, [verdict, tie]);


  if (connection.status === 'closed' || !view) {
    return <div className="overlay-root" />;
  }

  const people = view.players;
  const nameOf = (id: string) => people.find((p) => p.id === id)?.name ?? '—';
  const night = view.layout === 'NIGHT';
  const spotlightCount = spotlightIds.size;

  // Bis einschließlich 9 Spielern maximal 3 Kameras pro Reihe.
  // Erst ab 10 Spielern werden 4 Kameras pro Reihe verwendet.
  const perRow =
    people.length <= 3 ? Math.max(1, people.length) :
    people.length <= 9 ? 3 :
    4;
  const rows = Math.ceil(people.length / Math.max(perRow, 1));
  // Läuft die Wahl, rückt der Spielleiter nach links und das Wahlfenster stellt sich daneben.
  const electionRunning = !!view.mayorElection;
  const contentWidth = 92;
  const contentCenter = 50;

  /*
   * Die Kameras müssen in die Höhe passen, sonst überlagern sie Spielleiter und Überschrift.
   * Deshalb wird die Rahmenhöhe aus der Bildhöhe abgeleitet (in vh) und die Breite zusätzlich
   * über die Bildbreite gedeckelt. `min()` im CSS wählt jeweils den kleineren Wert.
   */
  /*
   * Vertikale Schutzzonen:
   * Hostkamera -> Überschrift/Banner -> Spielerkameras.
   * Die Spielergröße wird ausschließlich aus dem danach verbleibenden Platz berechnet.
   */
  const HEAD = view?.result || banner ? 13 : 7;

  // ============================================================
  // FESTES OVERLAY-LAYOUT
  //
  // Ziel:
  // - Spielerkameras deutlich größer
  // - Kameras dicht zusammen, aber niemals berühren
  // - Spielleiterkamera größer
  // - Überschrift klein und direkt darunter
  // - genügend Sicherheitsabstand zwischen Überschrift und Kameras
  // ============================================================

  const GAP = 0.45;

  // Banderole und Ergebnisanzeige ersetzen während ihrer Anzeige
  // die normale Überschrift.
  const result = view.result;
  const bannerVisible = !!banner || !!result;

  // Die endgültige Kamerahöhe wird weiter unten aus dem tatsächlich
  // verfügbaren vertikalen Platz berechnet.

  // Maximale Kamerabreite aus dem tatsächlich verfügbaren Platz.
  // Zwischen zwei Rahmen bleibt immer ein kleiner sichtbarer Abstand.
  const CAMERA_GAP_VW = 0.65;
  const dayWidthVw =
    (contentWidth - CAMERA_GAP_VW * Math.max(0, perRow - 1)) / perRow;

  // Die tatsächliche Höhe wird nach Berechnung von gridTop bestimmt.

  // ------------------------------------------------------------
  // SPIELLEITERKAMERA
  // ------------------------------------------------------------
  // Bewusst größer als eine normale Spielerkamera.
  const HOST_WIDTH_VW = 23.5;
  const HOST_HEIGHT_VH = (HOST_WIDTH_VW / 1.74) * (16 / 9);

  // Bürgermeisterwahl: Spielleiterkamera + Votingfenster bilden
  // gemeinsam einen horizontal zentrierten Block.
  const ELECTION_WIDTH_VW = 45;
  const ELECTION_GROUP_GAP_VW = 1.5;
  const ELECTION_GROUP_WIDTH_VW =
    HOST_WIDTH_VW + ELECTION_GROUP_GAP_VW + ELECTION_WIDTH_VW;

  const electionHostCenter =
    50 - ELECTION_GROUP_WIDTH_VW / 2 + HOST_WIDTH_VW / 2;

  const electionWindowLeft =
    50 - ELECTION_GROUP_WIDTH_VW / 2 +
    HOST_WIDTH_VW +
    ELECTION_GROUP_GAP_VW;

  const hostTop = 1.2;
  const hostBottom = hostTop + HOST_HEIGHT_VH;

  // ------------------------------------------------------------
  // ÜBERSCHRIFT / BANDEROLE
  // ------------------------------------------------------------
  // Ein gemeinsamer Slot zwischen Spielleiter- und Spielerkameras.
  // Die Banderole ERSETZT die Überschrift vollständig.
  //
  // Die Überschrift sitzt etwas näher an der Spielleiterkamera
  // als bisher.
  // Auf großen 16:9-Flächen rückt die Überschrift ca. 20px näher
  // an die Spielleiterkamera. Auf kleineren Browserfenstern bleibt
  // der bisherige Abstand erhalten.
  const largeViewportHeadingOffset =
    typeof window !== 'undefined' && window.innerHeight >= 900
      ? -1.70
      : 0.15;

  const headingTop = hostBottom + largeViewportHeadingOffset;

  const BANNER_WIDTH_VW = 34.5;
  const BANNER_HEIGHT_VH = (BANNER_WIDTH_VW / 3) * (16 / 9);

  // Die normale Überschrift bleibt an ihrer bisherigen Position.
  //
  // Die deutlich höhere Banderole darf näher an die Hostkamera rücken.
  // Auf 1080p sind 25px ungefähr 2.31vh.
  // Insgesamt jetzt ca. 45px näher an der Spielleiterkamera.
  const BANNER_TIGHTEN_VH = 4.16;
  const bannerTop = headingTop - BANNER_TIGHTEN_VH;

  // Normal: etwa 5px mehr Luft unter der Überschrift.
  // 5px entsprechen bei 1080p ungefähr 0.46vh.
  const NORMAL_HEADING_ZONE_VH = 3.46;

  // Während die Banderole sichtbar ist, wird ihre vollständige
  // Höhe reserviert. So kann sie weder Host- noch Spielerkameras
  // berühren.
  // Auch unterhalb der Banderole ziehen wir die Spieler um ca. 25px
  // näher heran. Der Wert darf negativ sein, weil die PNG-Banderole
  // innerhalb ihrer 3:1-Fläche bereits transparenten Rand besitzt.
  // Zusätzlich ca. 20px weniger Abstand zu den Spielerkameras.
  const BANNER_PLAYER_GAP_VH = -3.66;

  const gridTop = bannerVisible
    ? bannerTop + BANNER_HEIGHT_VH + BANNER_PLAYER_GAP_VH
    : headingTop + NORMAL_HEADING_ZONE_VH;

  // Maximale Höhe aus dem tatsächlich verbleibenden Bildschirmplatz.
  // Oben beginnt der Bereich bei gridTop, unten bleiben 1.2vh Reserve.
  // Zwischen den Reihen bleibt GAP frei.
  const GRID_BOTTOM_VH = 98.8;
  const availableGridHeight = Math.max(1, GRID_BOTTOM_VH - gridTop);
  const maxHeightFromRows =
    (availableGridHeight - GAP * Math.max(0, rows - 1)) /
    Math.max(1, rows);

  // Auch die horizontale Breite begrenzt die Höhe, damit das
  // Seitenverhältnis des Rahmens erhalten bleibt.
  const maxHeightFromWidth =
    (dayWidthVw / 1.74) * (16 / 9);

  const dayHeightVh = Math.min(maxHeightFromRows, maxHeightFromWidth);

  // Breite und Höhe stammen jetzt aus derselben tatsächlichen Größe.
  const dayWidth =
    `min(${dayWidthVw.toFixed(2)}vw, ${(dayHeightVh * 1.74).toFixed(2)}vh)`;

  const perSide = Math.max(1, Math.ceil(people.length / 2));
  const nightTileVh = Math.max(7, (88 - 1.5 * perSide) / perSide);
  const nightWidthVw = Math.min(13, dayWidthVw * 0.85);
  const nightWidth = `min(${nightWidthVw.toFixed(1)}vw, ${(nightTileVh * 1.74).toFixed(1)}vh)`;
  const nightHeightVh = Math.min(
    nightTileVh,
    (nightWidthVw / 1.74) * (16 / 9),
  );

  function edgePlace(index: number): { left: string; top: string } {
    const side = index < perSide ? 0 : 1;
    const slot = side === 0 ? index : index - perSide;
    const count = side === 0 ? perSide : people.length - perSide;
    const span = count * nightHeightVh + (count - 1) * 1.5;
    const start = Math.max(6, (100 - span) / 2) + nightHeightVh / 2;
    const y = count <= 1 ? 55 : start + slot * (nightHeightVh + 1.5);
    const margin = nightWidthVw / 2 + 2;

    return {
      left: side === 0 ? `${margin}%` : `${100 - margin}%`,
      top: `${y}%`,
    };
  }

  function placement(playerId: string, index: number): React.CSSProperties {
    if (night && spotlightIds.has(playerId)) {
      const order = [...spotlightIds].indexOf(playerId);
      const spotVw = Math.min(28, 82 / Math.max(spotlightCount, 1));
      const offset =
        (order - (spotlightCount - 1) / 2) * (spotVw + 1.5);

      return {
        left: `calc(50% + ${offset}vw)`,
        top: '58%',
        width: `min(${spotVw}vw, 46vh)`,
        transform: 'translate(-50%, -50%)',
      };
    }

    if (night) {
      return {
        ...edgePlace(index),
        width: nightWidth,
        transform: 'translate(-50%, -50%)',
        opacity: 1,
      };
    }

    const row = Math.floor(index / perRow);
    const col = index % perRow;
    const inRow = Math.min(perRow, people.length - row * perRow);

    // Die Kamera kann durch die verfügbare Höhe kleiner sein als
    // dayWidthVw. Für die Positionierung verwenden wir deshalb ihre
    // TATSÄCHLICHE Breite. Zwischen den Rahmen bleiben ca. 10 px.
    const actualDayWidthVw = Math.min(
      dayWidthVw,
      dayHeightVh * 1.74 * (9 / 16),
    );

    // Der Rahmen selbst besitzt Ornamente, die optisch über die
    // eigentliche Kameraöffnung hinausragen. Deshalb braucht nicht
    // nur die Kamera, sondern der komplette Rahmen eine Schutzzone.
    //
    // Horizontal etwas mehr Luft zwischen den Rahmen,
    // vertikal bewusst deutlich enger.
    //
    // bei 1920x1080:
    // 2.29vw ~= 44px horizontal
    // 1.39vh ~= 15px vertikal
    const FRAME_SAFE_GAP_VW = 2.45;
    const FRAME_SAFE_GAP_VH = -0.65;

    const isWinner =
      !!result?.winnerPlayerIds.includes(playerId);

    // Nur in der Siegesansicht:
    // Gewinner bleiben 12 % größer, Verlierer werden 15 % kleiner.
    const endScale =
      result
        ? (isWinner ? 1.12 : 0.85)
        : 1;

    // Horizontal bleibt der sichere Abstand der Siegesansicht erhalten.
    // So können auch vergrößerte Gewinnerrahmen niemals kollidieren.
    const resultLayoutScale = result ? 1.12 : 1;

    const DAY_COLUMN_STEP =
      actualDayWidthVw * resultLayoutScale + FRAME_SAFE_GAP_VW;

    const x =
      contentCenter +
      (col - (inRow - 1) / 2) * DAY_COLUMN_STEP;

    // Vertikale Position:
    // Im normalen Spiel bleibt das bisherige Raster unverändert.
    //
    // In der Siegesansicht wird dagegen für JEDE Reihe die tatsächlich
    // größte Kamera berücksichtigt. Gewinner = 112 %, Verlierer = 85 %.
    // Die nächste Reihe beginnt erst unterhalb des größten Rahmens der
    // vorherigen Reihe. Dadurch können die Rahmen niemals kollidieren.
    let y: number;

    if (result) {
      // Siegesansicht:
      // Gewinner bleiben deutlich größer (112 %), Verlierer 85 %.
      // Gleichzeitig wird das GESAMTE Raster automatisch so weit
      // verkleinert, dass auch die letzte Reihe vollständig sichtbar
      // bleibt. Die Abstände zwischen den Rahmen bleiben erhalten.
      const RESULT_VERTICAL_GAP_VH = 0.55;
      const RESULT_BOTTOM_VH = 98.0;

      const rowScale = (rowIndex: number) => {
        const start = rowIndex * perRow;
        const end = Math.min(start + perRow, people.length);

        let largestScale = 0.85;

        for (let i = start; i < end; i += 1) {
          if (result.winnerPlayerIds.includes(people[i].id)) {
            largestScale = 1.12;
            break;
          }
        }

        return largestScale;
      };

      // Gesamthöhe aller Reihen bei unveränderter Kameragröße.
      let naturalResultHeight = 0;

      for (let r = 0; r < rows; r += 1) {
        naturalResultHeight += dayHeightVh * rowScale(r);
      }

      naturalResultHeight +=
        RESULT_VERTICAL_GAP_VH * Math.max(0, rows - 1);

      // Nur falls nötig wird das komplette Siegesraster proportional
      // zusammengeschoben. Dadurch wird unten nichts abgeschnitten.
      const availableResultHeight =
        Math.max(1, RESULT_BOTTOM_VH - gridTop);

      const resultFitScale =
        Math.min(1, availableResultHeight / naturalResultHeight);

      const fittedGap =
        RESULT_VERTICAL_GAP_VH * resultFitScale;

      const fittedRowHeight = (rowIndex: number) =>
        dayHeightVh * rowScale(rowIndex) * resultFitScale;

      let centerY =
        gridTop + fittedRowHeight(0) / 2;

      for (let r = 1; r <= row; r += 1) {
        centerY +=
          fittedRowHeight(r - 1) / 2 +
          fittedGap +
          fittedRowHeight(r) / 2;
      }

      y = centerY;
    } else {
      y =
        gridTop +
        dayHeightVh / 2 +
        row * (dayHeightVh + FRAME_SAFE_GAP_VH);
    }

    // Die eigentliche Kamera muss denselben Fit erhalten wie ihre
    // Position im Ergebnisraster. Gewinner bleiben relativ zu den
    // Verlierern weiterhin deutlich größer.
    let finalScale = endScale;

    if (result) {
      const RESULT_VERTICAL_GAP_VH = 0.55;
      const RESULT_BOTTOM_VH = 98.0;

      const resultRowScale = (rowIndex: number) => {
        const start = rowIndex * perRow;
        const end = Math.min(start + perRow, people.length);

        for (let i = start; i < end; i += 1) {
          if (result.winnerPlayerIds.includes(people[i].id)) {
            return 1.12;
          }
        }

        return 0.85;
      };

      let naturalHeight = 0;

      for (let r = 0; r < rows; r += 1) {
        naturalHeight += dayHeightVh * resultRowScale(r);
      }

      naturalHeight +=
        RESULT_VERTICAL_GAP_VH * Math.max(0, rows - 1);

      const availableHeight =
        Math.max(1, RESULT_BOTTOM_VH - gridTop);

      const fit =
        Math.min(1, availableHeight / naturalHeight);

      finalScale *= fit;
    }

    return {
      left: `${x}%`,
      top: `${y}%`,
      width: dayWidth,
      transform: `translate(-50%, -50%) scale(${finalScale})`,
      zIndex: result && isWinner ? 5 : undefined,
    };
  }

  /** Der wichtigste Sieger bestimmt Farbe und Konfetti. */
  const celebration = result
    ? (['DEMON', 'FOOL', 'LOVERS', 'VAMPIRE', 'VILLAGE'].find((w) => result.winners.includes(w as never)) ?? null)
    : null;
  const rolesById = new Map((result?.roles ?? []).map((r) => [r.playerId, r]));

  /** Fester Platz am Rand – unabhängig davon, wer gerade aufgerufen ist. */
  const votes = view.dayVote?.revealed ? view.dayVote.counts : null;
  const morningDead = new Set(view.morning?.deadPlayerIds ?? []);

  const mood = ambienceFor(view.layout, view.phase);
  const heading =
    view.phase === 'GAME_END'
      ? winSentence(result?.winners ?? [])
      : night
        ? `Nacht ${view.night?.number ?? Math.max(1, view.round)}`
        : view.phase === 'DAY_VOTE' ||
            view.phase === 'VOTE_REVEAL' ||
            view.phase === 'MAYOR_TIEBREAK' ||
            view.phase === 'DAY_VOTE_RESOLUTION'
          ? 'Abstimmung'
          : view.phase === 'MAYOR_ELECTION'
            ? 'Bürgermeisterwahl'
            : view.layout === 'LOBBY'
              ? 'Noxbury'
              : `Tag ${Math.max(1, view.round)}`;


  return (
    <div className="overlay-root">
      {/*
        Beide Stimmungen liegen übereinander und werden über die Deckkraft gewechselt. So entsteht
        eine durchgehende Überblendung ohne schwarzes Zwischenbild. Das Bild aus dem Medienplatz
        liegt über dem Farbverlauf; fehlt es, bleibt allein der Verlauf stehen.
      */}
      <div className="sky">
        {(['START', 'DAY', 'DUSK', 'NIGHT'] as const).map((phase) => (
          <div
            key={phase}
            className={`sky-layer sky-${phase.toLowerCase()}`}
            style={{
              opacity: mood === phase ? 1 : 0,
              transitionDuration: `${CROSSFADE_MS}ms`,
              backgroundImage:
                view.layout === 'LOBBY'
                  ? "url('/Lobby.png')"
                  : `url('${MEDIA[phase].image}')`,
            }}
          />
        ))}
      </div>

      <div className={`overlay-veil ${view.layout.toLowerCase()}`} />

      {celebration && (
        <>
          <div
            className={`celebration-veil win-${celebration.toLowerCase()}`}
            style={{ backgroundImage: `url('${winImage(celebration)}')`, animationDuration: `${FADE_IN_MS}ms` }}
          />
          <Confetti winner={celebration} />
        </>
      )}

      {people.map((p, i) => {
        const cam = view.cameras.find((c) => c.playerId === p.id);
        const reveal = rolesById.get(p.id);
        // Vor der Auflösung verrät nur die Besessenheit etwas – der Geist trägt seinen eigenen Rahmen.
        const frameClass = reveal
          ? reveal.lover
            ? 'frame-lovers'
            : (FRAME_CLASS[reveal.role ?? ''] ?? '')
          : p.ghostLetters.length > 0
            ? 'frame-ghost'
            : p.possessed
              ? 'frame-possessed'
              : '';
        // Nachttote bleiben bis zum Aufdecken unverändert stehen.
        const dead = p.dead && !(isMorning && !morningRevealed && morningDead.has(p.id));
        const classes = [
          'cam',
          frameClass,
          reveal ? 'reveal' : '',
          night && spotlightIds.has(p.id) ? 'spotlight' : '',
          dead ? 'dead' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <div key={p.id} className={classes} style={placement(p.id, i)}>
            <div className="lens">
              {cam?.viewUrl ? (
                <AdaptiveCamera
                  url={cam.viewUrl}
                  title={p.name}
                  priority={night && spotlightIds.has(p.id) ? 'spotlight' : 'tile'}
                  cameraToken={cameraToken}
                />
              ) : (
                <div className="empty">ohne Bild</div>
              )}
              <Tally count={votes?.find((c) => c.playerId === p.id)?.votes ?? 0} />
              {view.phase === 'MAYOR_ELECTION' && (
                <div className="mayor-camera-number" aria-label={`Spieler ${p.number}`}>
                  {p.number}
                </div>
              )}
              {p.ghostLetters.length > 0 && (
                <div className="ghost-letters" aria-label="Botschaft des Geistes">
                  {p.ghostLetters.map((letter, i) => (
                    <span key={i}>{letter}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="frame-art" />
            <div className="plate">
              <span>{p.name}</span>
              {reveal?.role && <span className="role-note">· {roleName(reveal.role)}</span>}
              {p.isMayor && !reveal && <span aria-hidden="true">★</span>}
            </div>
          </div>
        );
      })}

      {view.hostCamera.viewUrl && (
        <div
          className="cam host-cam"
          title="Spielleiter"
          style={{
            width: `min(${HOST_WIDTH_VW.toFixed(1)}vw, ${(HOST_HEIGHT_VH * 1.74).toFixed(1)}vh)`,
            left: electionRunning ? `${electionHostCenter}%` : `${contentCenter}%`,
            top: `${hostTop}%`,
          }}
        >
          <div className="lens">
            <AdaptiveCamera
              url={view.hostCamera.viewUrl}
              title={view.hostName}
              priority="host"
              cameraToken={cameraToken}
            />
          </div>
          <div className="frame-art" />
          <div className="plate">
            <span>{view.hostName}</span>
          </div>
        </div>
      )}

      {view.phase !== 'GAME_END' && !bannerVisible && (
        <div
          className={`phase-word${!night && view.layout !== 'LOBBY' && !view.phase.includes('VOTE') && view.phase !== 'MAYOR_ELECTION' ? ' day-heading' : ''}`}
          style={{
            top: `${headingTop}%`,
            left: `${contentCenter}%`,
            fontSize: 'clamp(16px, 1.45vw, 24px)',
          }}
        >
          {heading}
          {view.paused && <span className="sub"> · angehalten</span>}
          {view.night?.announcement && (
            <div className="call fade-in" key={view.night.step ?? 'none'}>
              {view.night.announcement}
            </div>
          )}
        </div>
      )}

      {(result || banner) && (
        <div
          className="win-banner banner-in"
          style={{
            top: `${bannerTop}%`,
            left: `${contentCenter}%`,
            width: `${BANNER_WIDTH_VW}vw`,
          }}
        >
          <BannerText text={result ? winSentence(result.winners) : (banner ?? '')} />
        </div>
      )}

      {view.mayorElection && (
        <div
          className={`election-window${view.mayorElection.tally.length <= 9 ? ' election-window-3x3' : ''}`}
          style={{
            left: `${electionWindowLeft}%`,
            top: `${hostTop + HOST_HEIGHT_VH / 2}%`,
            width: `${ELECTION_WIDTH_VW}vw`,
            transform: 'translateY(-50%)',
          }}
        >
          <div className="election-header">
            <span className="election-title">Bürgermeisterwahl</span>
            <span className="election-hint">
              !bm {'<'}Nummer{'>'} um deinen Favoriten zu wählen
            </span>
          </div>
          <div className="election-table">
            {Array.from({ length: view.mayorElection.tally.length }, (_, i) => {
              // Die Anzeige bleibt immer auf der festen Spielernummer.
              // Stimmen verändern nur Balken/Zähler, niemals die Position.
              const entry = view.mayorElection!.tally.find((candidate) => candidate.number === i + 1);
              const share = entry ? Math.round((entry.votes / Math.max(1, view.mayorElection!.totalVotes)) * 100) : 0;
              return (
                <div key={entry?.playerId ?? `leer-${i}`} className={`election-row${entry ? '' : ' empty'}`}>
                  <span className="election-name">
                    {i + 1}.{entry ? ` ${nameOf(entry.playerId)}` : ''}
                  </span>
                  <span className="chart-bar">{entry && <span style={{ width: `${share}%` }} />}</span>
                  <span className="chart-value">{entry ? entry.votes : 0}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

   </div>
  );
}
